import { useCallback, useEffect, useRef, useState } from 'react';
import { answerAgent, TOUR_STEPS, type AgentAction } from '../../lib/agentBrain';
import { useCurrentWorkspace } from '../../lib/workspace';

export interface AgentMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  actions?: AgentAction[];
  /** false → the bubble also offers human / AI escalation. */
  confident?: boolean;
  timestamp: string;
}

const uid = () => crypto.randomUUID().slice(0, 10);
const now = () => new Date().toISOString();

// ── Persistence (per workspace, mirrors the AI chat) ──────────────────────────
const storageKey = (wsId: string) => `iheart:domina-agent:${wsId}`;

function loadHistory(wsId: string | undefined): AgentMessage[] {
  if (!wsId) return [];
  try {
    const raw = localStorage.getItem(storageKey(wsId));
    const parsed = raw ? (JSON.parse(raw) as AgentMessage[]) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHistory(wsId: string | undefined, msgs: AgentMessage[]) {
  if (!wsId) return;
  try {
    localStorage.setItem(storageKey(wsId), JSON.stringify(msgs));
  } catch {
    /* localStorage full/disabled — ignore */
  }
}

/**
 * Local, zero-cost chat engine for Domina Agent. No network, no tokens — every
 * reply comes from `agentBrain`. Mirrors the shape of `useDominaChat` (incl.
 * per-workspace history that survives reloads), plus a scripted tour.
 */
export function useDominaAgent() {
  const ws = useCurrentWorkspace();
  const wsId = ws?.publicId;
  const wsIdRef = useRef(wsId);
  wsIdRef.current = wsId;

  const [messages, setMessages] = useState<AgentMessage[]>(() => loadHistory(wsId));
  const [isTyping, setIsTyping] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Reload history when the workspace changes.
  const prevWsId = useRef(wsId);
  useEffect(() => {
    if (prevWsId.current !== wsId) {
      prevWsId.current = wsId;
      setMessages(loadHistory(wsId));
    }
  }, [wsId]);

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    },
    [],
  );

  /** Append + persist in one step (always writes to the current workspace). */
  const push = useCallback((msg: AgentMessage) => {
    setMessages((prev) => {
      const next = [...prev, msg];
      saveHistory(wsIdRef.current, next);
      return next;
    });
  }, []);

  /** Push an assistant message after a short, natural "typing" pause. */
  const say = useCallback(
    (content: string, actions?: AgentAction[], confident = true) => {
      setIsTyping(true);
      const delay = Math.min(1100, 350 + content.length * 4);
      const t = setTimeout(() => {
        push({ id: uid(), role: 'assistant', content, actions, confident, timestamp: now() });
        setIsTyping(false);
      }, delay);
      timers.current.push(t);
    },
    [push],
  );

  /**
   * Show a tour step. Stateless by design — each "Next →" carries its own
   * target index (stepIndex), so the button keeps working even after a reload
   * (the chat history persists, but any in-memory tour position wouldn't).
   */
  const showTourStep = useCallback(
    (index: number) => {
      const step = TOUR_STEPS[index];
      if (!step) return;
      const hasNext = index < TOUR_STEPS.length - 1;
      const actions: AgentAction[] = [
        ...(step.actions ?? []),
        ...(hasNext ? [{ label: 'Next →', kind: 'tour-next' as const, stepIndex: index + 1 }] : []),
      ];
      say(step.text, actions);
    },
    [say],
  );

  const startTour = useCallback(() => {
    showTourStep(0);
  }, [showTourStep]);

  /** Send a free-text message (from the input or a quick-reply chip). */
  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      push({ id: uid(), role: 'user', content: trimmed, timestamp: now() });
      const reply = answerAgent(trimmed);
      say(reply.text, reply.actions, reply.confident);
    },
    [push, say],
  );

  const clearHistory = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setMessages([]);
    setIsTyping(false);
    const id = wsIdRef.current;
    if (id) {
      try {
        localStorage.removeItem(storageKey(id));
      } catch {
        /* ignore */
      }
    }
  }, []);

  return { messages, isTyping, sendMessage, startTour, showTourStep, clearHistory };
}
